import { useEffect, useState } from "react";
import Router from "next/router";
import {
  AlignItems,
  Box,
  Color,
  FontWeight,
  HoverEffect,
  JustifyContent,
  P,
  PlainLink,
  Visibility,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";

import ForcedPasswordReset from "../ForcedPasswordReset";

import { SignInMarketing } from "./marketing/SignInMarketing";
import { useSignIn } from "./useSignIn";
import { SignInForm } from "./SignInForm";

import MaintenanceNotice from "src/components/maintenance-notice/MaintenanceNotice";
import { Route } from "src/components/routing";
import { getRouteURL } from "src/common/routing";
import { CountryCodeSupported } from "src/common/country";
import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { VerticalSplitPageLayout } from "src/components/layout/VerticalSplitPage/VerticalSplitPageLayout";

interface SignInProps {
  defaultCountryCode: CountryCodeSupported;
}

export const SignUpLink = () => (
  <P size={3}>
    <Trans id="New to GoCardless"> New to GoCardless? </Trans>{" "}
    <PlainLink
      textDecoration="underline"
      effect={HoverEffect.TextDecoration}
      href={getRouteURL({
        route: Route.SignUp,
      })}
      weight={FontWeight.SemiBold}
    >
      <Trans id="Sign up">Sign up</Trans>
    </PlainLink>
  </P>
);

export const SignIn: React.FC<SignInProps> = ({ defaultCountryCode }) => {
  const searchParams = new URLSearchParams(window.location.search);

  const [recoveryCode, setRecoveryCode] = useState(false);
  const [usingSso, setUsingSso] = useState(searchParams.has("sso"));
  const { sendEvent } = useSegment();

  const {
    submitSignInWithPassword,
    submitSignInWithSso,
    authFactorMetaData,
    forcedPasswordResetMetaData,
    requireResetPassword,
    tooManyFailedAttempts,
    isSubmitting,
    ssoNotAvailable,
    setSsoNotAvailable,
  } = useSignIn();

  const showForcedPasswordReset =
    forcedPasswordResetMetaData && !authFactorMetaData;

  const triggerRecoveryCode = () => {
    setRecoveryCode(true);
  };

  const triggerOneTimePassword = () => {
    setRecoveryCode(false);
  };

  useEffect(() => {
    const pageViewData = {
      business_location: defaultCountryCode,
    };
    sendEvent(TrackingEvent.SIGNIN_PAGE_VIEWED, pageViewData);
  }, [defaultCountryCode, sendEvent]);

  // If an error occurs during OAuth login, Routes::Sso::Failure will redirect back here with the message in a `oauth_error_key` param
  const oauthErrorKey = searchParams.get("oauth_error_key");

  const rightContent = (
    <Box
      layout="flex"
      width="100%"
      height="100%"
      alignItems={[AlignItems.Start, null, null, AlignItems.Center]}
      justifyContent={JustifyContent.Center}
    >
      <SignInForm
        authFactorMetaData={authFactorMetaData}
        hasRecoveryCode={!!recoveryCode}
        requireResetPassword={requireResetPassword}
        tooManyFailedAttempts={tooManyFailedAttempts}
        ssoNotAvailable={ssoNotAvailable}
        setSsoNotAvailable={setSsoNotAvailable}
        isSubmitting={isSubmitting}
        onSubmitWithPassword={submitSignInWithPassword}
        onSubmitWithSso={submitSignInWithSso}
        onTriggerRecoveryCode={triggerRecoveryCode}
        onTriggerOneTimePassword={triggerOneTimePassword}
        usingSso={usingSso}
        setUsingSso={setUsingSso}
        oauthErrorKey={oauthErrorKey}
      />
    </Box>
  );

  if (showForcedPasswordReset) {
    return (
      <ForcedPasswordReset
        forcedPasswordResetMetaData={forcedPasswordResetMetaData}
        submit={submitSignInWithPassword}
        handleSignOut={() => Router.reload()}
      />
    );
  }

  return (
    <Box>
      <MaintenanceNotice />
      <VerticalSplitPageLayout
        rightContent={rightContent}
        leftContent={<SignInMarketing />}
        leftBackground={{ backgroundColor: Color.Ultraviolet_50 }}
        topRightButton={
          <Visibility visible={["none", null, "block"]}>
            <SignUpLink />
          </Visibility>
        }
      />
    </Box>
  );
};
